// The Şekiller catalog: every shape the Bileşenler → Şekiller panel offers.
// A box stores only `state.shapeId`; geometry comes from here at render time,
// so adding a shape is one entry. Never rename an id — saved courses use them.
import {
  cutCornerPath,
  documentPath,
  halfDiscPath,
  quarterDiscPath,
  regularPolygonPoints,
  roundedRectCornersPath,
  speechBubbleLeftPath,
  speechBubblePath,
  starPoints,
  ticketPath,
} from "./geometry";

export const SHAPE_CATEGORIES = [
  { key: "temel", label: { tr: "Temel", en: "Basic", es: "Básicas" } },
  { key: "kartlar", label: { tr: "Kartlar", en: "Cards", es: "Tarjetas" } },
  { key: "cizgiler", label: { tr: "Çizgiler", en: "Lines", es: "Líneas" } },
  { key: "oklar", label: { tr: "Oklar", en: "Arrows", es: "Flechas" } },
  { key: "cagri", label: { tr: "Çağrı kutuları", en: "Callouts", es: "Globos" } },
  { key: "dekoratif", label: { tr: "Dekoratif", en: "Decorative", es: "Decorativas" } },
  { key: "simgeler", label: { tr: "Simgeler", en: "Symbols", es: "Símbolos" } },
];

const L = (tr, en, es) => ({ tr, en, es });
const round2 = (n) => Math.round(n * 100) / 100;
// Box sizes are % of the 16:9 canvas, so a square needs h = w * 16 / 9.
const sq = (w) => ({ w: `${w}%`, h: `${round2((w * 16) / 9)}%` });
const box = (w, h) => ({ w: `${w}%`, h: `${h}%` });

const SOLID = { fill: true, stroke: true, radius: false, text: true };
const ROUNDABLE = { fill: true, stroke: true, radius: true, text: true };
const LINE = { fill: false, stroke: true, radius: false, text: false };
const ICON = { fill: true, stroke: true, radius: false, text: false };

const rect = (id, label, radius, size = box(20, 20)) => ({
  id,
  label,
  kind: "rect",
  radius,
  size,
  caps: ROUNDABLE,
});
const poly = (id, label, points, size = sq(14), caps = ROUNDABLE) => ({
  id,
  label,
  kind: "poly",
  points,
  radius: 0,
  size,
  caps,
});
const custom = (id, label, build, radius, size = box(20, 20), caps = ROUNDABLE, extra = {}) => ({
  id,
  label,
  kind: "custom",
  build,
  radius,
  size,
  caps,
  ...extra,
});
const path = (id, label, d, size = sq(14)) => ({ id, label, kind: "path", d, size, caps: SOLID });

// Lines draw between the inset box's edges; `sw` is the stroke width, used by
// the double line to space its two strokes.
const H = (x, y, w, h) => `M${x} ${y + h / 2}H${x + w}`;
const line = (id, label, build, opts = {}, size = box(25, 6)) => ({
  id,
  label,
  kind: "line",
  build,
  start: opts.start || "none",
  end: opts.end || "none",
  dash: opts.dash || "solid",
  cap: opts.cap || "butt",
  strokeWidth: opts.strokeWidth || 3,
  size,
  caps: LINE,
});
const icon = (id, label, key) => ({ id, label, kind: "icon", icon: key, size: sq(8), caps: ICON });

const TEMEL = [
  rect("rect", L("Dikdörtgen", "Rectangle", "Rectángulo"), 0),
  rect("rect-rounded", L("Yuvarlak dikdörtgen", "Rounded rectangle", "Rectángulo redondeado"), 24),
  rect("rect-soft", L("Yumuşak dikdörtgen", "Soft rectangle", "Rectángulo suave"), 10),
  rect("squircle", L("Squircle", "Squircle", "Squircle"), "squircle", sq(14)),
  rect("capsule", L("Kapsül", "Capsule", "Cápsula"), "pill", box(22, 10)),
  {
    id: "circle",
    label: L("Daire", "Circle", "Círculo"),
    kind: "ellipse",
    size: sq(14),
    caps: SOLID,
  },
  {
    id: "ellipse",
    label: L("Elips", "Ellipse", "Elipse"),
    kind: "ellipse",
    size: box(22, 18),
    caps: SOLID,
  },
  custom(
    "half-circle",
    L("Yarım daire", "Half circle", "Semicírculo"),
    halfDiscPath,
    0,
    box(18, 16),
    SOLID
  ),
  custom(
    "quarter-circle",
    L("Çeyrek daire", "Quarter circle", "Cuarto de círculo"),
    quarterDiscPath,
    0,
    sq(12),
    SOLID
  ),
  poly("triangle", L("Üçgen", "Triangle", "Triángulo"), [
    [0.5, 0],
    [1, 1],
    [0, 1],
  ]),
  poly("triangle-down", L("Ters üçgen", "Inverted triangle", "Triángulo invertido"), [
    [0, 0],
    [1, 0],
    [0.5, 1],
  ]),
  poly("diamond", L("Elmas", "Diamond", "Rombo"), [
    [0.5, 0],
    [1, 0.5],
    [0.5, 1],
    [0, 0.5],
  ]),
  poly("pentagon", L("Beşgen", "Pentagon", "Pentágono"), regularPolygonPoints(5)),
  poly("hexagon", L("Altıgen", "Hexagon", "Hexágono"), [
    [0.25, 0],
    [0.75, 0],
    [1, 0.5],
    [0.75, 1],
    [0.25, 1],
    [0, 0.5],
  ]),
  poly("octagon", L("Sekizgen", "Octagon", "Octágono"), [
    [0.3, 0],
    [0.7, 0],
    [1, 0.3],
    [1, 0.7],
    [0.7, 1],
    [0.3, 1],
    [0, 0.7],
    [0, 0.3],
  ]),
  poly(
    "parallelogram",
    L("Paralelkenar", "Parallelogram", "Paralelogramo"),
    [
      [0.22, 0],
      [1, 0],
      [0.78, 1],
      [0, 1],
    ],
    box(20, 16)
  ),
  poly(
    "trapezoid",
    L("Yamuk", "Trapezoid", "Trapecio"),
    [
      [0.2, 0],
      [0.8, 0],
      [1, 1],
      [0, 1],
    ],
    box(20, 16)
  ),
];

const KARTLAR = [
  rect("card-soft", L("Yumuşak kart", "Soft card", "Tarjeta suave"), 14, box(24, 30)),
  custom(
    "card-cut",
    L("Kesik köşeli panel", "Cut-corner panel", "Panel de esquina cortada"),
    cutCornerPath,
    28,
    box(24, 30)
  ),
  custom(
    "card-tab",
    L("Sekme", "Tab", "Pestaña"),
    (x, y, w, h, r) => roundedRectCornersPath(x, y, w, h, [r, r, 0, 0]),
    8,
    box(24, 30)
  ),
  custom(
    "card-label",
    L("Etiket", "Label", "Etiqueta"),
    (x, y, w, h, r) => roundedRectCornersPath(x, y, w, h, [r, r, 0, 0]),
    28,
    box(24, 30)
  ),
  custom("card-ticket", L("Bilet", "Ticket", "Ticket"), ticketPath, 8, box(24, 30)),
  {
    ...rect("card-frame", L("Yumuşak çerçeve", "Soft frame", "Marco suave"), 14, box(24, 30)),
    defaults: { fill: { type: "none" }, stroke: { width: 3 } },
  },
];

const CIZGILER = [
  line("line-thin", L("İnce Çizgi", "Thin line", "Línea fina"), H, { strokeWidth: 1 }),
  line("line-horizontal", L("Yatay Çizgi", "Horizontal line", "Línea horizontal"), H),
  line("line-round", L("Yuvarlak Uçlu Çizgi", "Rounded line", "Línea redondeada"), H, {
    strokeWidth: 8,
    cap: "round",
  }),
  line(
    "line-arrow-right",
    L("Sağ Ucu Oklu Çizgi", "Arrow line (right)", "Línea con flecha (derecha)"),
    H,
    {
      end: "arrow",
    }
  ),
  line(
    "line-arrow-left",
    L("Sol Ucu Oklu Çizgi", "Arrow line (left)", "Línea con flecha (izquierda)"),
    H,
    {
      start: "arrow",
    }
  ),
  line(
    "line-arrow-both",
    L("Çift Uçlu Ok Çizgisi", "Double arrow line", "Línea de doble flecha"),
    H,
    {
      start: "arrow",
      end: "arrow",
    }
  ),
  line("line-dot-ends", L("Daire Uçlu Çizgi", "Dot-ended line", "Línea con puntos"), H, {
    start: "circle",
    end: "circle",
  }),
  line(
    "line-dot-arrow",
    L("Daire-Ok Bağlantısı", "Dot-to-arrow connector", "Conector punto-flecha"),
    H,
    {
      start: "circle",
      end: "arrow",
    }
  ),
  line(
    "line-vertical",
    L("Dikey Çizgi", "Vertical line", "Línea vertical"),
    (x, y, w, h) => `M${x + w / 2} ${y}V${y + h}`,
    {},
    box(3, 30)
  ),
  line(
    "line-diagonal-down",
    L("Çapraz Çizgi ↘", "Diagonal line ↘", "Línea diagonal ↘"),
    (x, y, w, h) => `M${x} ${y}L${x + w} ${y + h}`,
    { strokeWidth: 2 },
    box(16, 22)
  ),
  line(
    "line-diagonal-up",
    L("Çapraz Çizgi ↗", "Diagonal line ↗", "Línea diagonal ↗"),
    (x, y, w, h) => `M${x} ${y + h}L${x + w} ${y}`,
    { strokeWidth: 2 },
    box(16, 22)
  ),
  line("line-dashed", L("Kesik Çizgi", "Dashed line", "Línea discontinua"), H, { dash: "dashed" }),
  line("line-dotted", L("Noktalı Çizgi", "Dotted line", "Línea punteada"), H, {
    dash: "dotted",
    strokeWidth: 4,
  }),
  line(
    "line-double",
    L("Çift Çizgi", "Double line", "Línea doble"),
    (x, y, w, h, sw) => {
      const gap = Math.max(3, sw * 1.6);
      const cy = y + h / 2;
      return `M${x} ${cy - gap / 2}H${x + w}M${x} ${cy + gap / 2}H${x + w}`;
    },
    { strokeWidth: 2 }
  ),
  line(
    "line-elbow",
    L("Dirsek Çizgisi", "Elbow line", "Línea en codo"),
    (x, y, w, h) => `M${x} ${y}H${x + w / 2}V${y + h}H${x + w}`,
    { strokeWidth: 2 },
    box(18, 20)
  ),
  line(
    "line-curve",
    L("Kavisli Çizgi", "Curved line", "Línea curva"),
    (x, y, w, h) =>
      `M${x} ${y + h * 0.75}C${x + w * 0.35} ${y - h * 0.15} ${x + w * 0.6} ${y + h * 1.1} ${x + w} ${
        y + h * 0.25
      }`,
    { strokeWidth: 2 },
    box(22, 14)
  ),
];

const OKLAR = [
  poly(
    "arrow-right",
    L("Sağ Ok", "Right arrow", "Flecha derecha"),
    [
      [0, 0.3],
      [0.6, 0.3],
      [0.6, 0],
      [1, 0.5],
      [0.6, 1],
      [0.6, 0.7],
      [0, 0.7],
    ],
    box(18, 18)
  ),
  poly(
    "arrow-left",
    L("Sol Ok", "Left arrow", "Flecha izquierda"),
    [
      [1, 0.3],
      [0.4, 0.3],
      [0.4, 0],
      [0, 0.5],
      [0.4, 1],
      [0.4, 0.7],
      [1, 0.7],
    ],
    box(18, 18)
  ),
  poly(
    "arrow-up",
    L("Yukarı Ok", "Up arrow", "Flecha arriba"),
    [
      [0.3, 1],
      [0.3, 0.4],
      [0, 0.4],
      [0.5, 0],
      [1, 0.4],
      [0.7, 0.4],
      [0.7, 1],
    ],
    box(10, 28)
  ),
  poly(
    "arrow-down",
    L("Aşağı Ok", "Down arrow", "Flecha abajo"),
    [
      [0.3, 0],
      [0.3, 0.6],
      [0, 0.6],
      [0.5, 1],
      [1, 0.6],
      [0.7, 0.6],
      [0.7, 0],
    ],
    box(10, 28)
  ),
  poly(
    "arrow-left-right",
    L("Sağ-Sol Ok", "Left-right arrow", "Flecha izquierda-derecha"),
    [
      [0, 0.5],
      [0.3, 0],
      [0.3, 0.3],
      [0.7, 0.3],
      [0.7, 0],
      [1, 0.5],
      [0.7, 1],
      [0.7, 0.7],
      [0.3, 0.7],
      [0.3, 1],
    ],
    box(22, 18)
  ),
  poly(
    "arrow-up-down",
    L("Yukarı-Aşağı Ok", "Up-down arrow", "Flecha arriba-abajo"),
    [
      [0.5, 0],
      [1, 0.3],
      [0.7, 0.3],
      [0.7, 0.7],
      [1, 0.7],
      [0.5, 1],
      [0, 0.7],
      [0.3, 0.7],
      [0.3, 0.3],
      [0, 0.3],
    ],
    box(10, 32)
  ),
  poly(
    "chevron-right",
    L("Sağ Chevron", "Right chevron", "Chevrón derecho"),
    [
      [0.15, 0],
      [0.45, 0],
      [0.9, 0.5],
      [0.45, 1],
      [0.15, 1],
      [0.6, 0.5],
    ],
    box(9, 22)
  ),
  poly(
    "chevron-left",
    L("Sol Chevron", "Left chevron", "Chevrón izquierdo"),
    [
      [0.85, 0],
      [0.55, 0],
      [0.1, 0.5],
      [0.55, 1],
      [0.85, 1],
      [0.4, 0.5],
    ],
    box(9, 22)
  ),
  path(
    "arrow-curved-right",
    L("Kıvrımlı Sağ Ok", "Curved right arrow", "Flecha curva derecha"),
    "M60 8L96 40L60 72V54C36 54 18 62 6 90C6 56 24 32 60 28Z"
  ),
];

const CAGRI = [
  custom(
    "callout-speech",
    L("Konuşma Balonu", "Speech bubble", "Bocadillo"),
    speechBubblePath,
    18,
    box(22, 26)
  ),
  custom(
    "callout-speech-left",
    L("Sol Konuşma Balonu", "Speech bubble (left)", "Bocadillo (izquierda)"),
    speechBubbleLeftPath,
    18,
    box(24, 22)
  ),
  path(
    "callout-cloud",
    L("Bulut", "Cloud", "Nube"),
    "M25 86C10 86 2 75 4 62C6 50 16 44 26 46C26 30 40 18 55 20C68 22 76 32 78 42C90 42 98 52 97 64C96 77 86 86 75 86Z",
    box(22, 26)
  ),
  poly(
    "callout-ribbon",
    L("Şerit", "Ribbon", "Cinta"),
    [
      [0, 0],
      [1, 0],
      [1, 1],
      [0.5, 0.78],
      [0, 1],
    ],
    box(10, 26)
  ),
  poly("callout-badge", L("Rozet", "Badge", "Insignia"), starPoints(16, 0.82), sq(14)),
  custom("callout-document", L("Doküman", "Document", "Documento"), documentPath, 6, box(14, 32)),
];

const DEKORATIF = [
  poly("star", L("Yıldız", "Star", "Estrella"), starPoints(5, 0.4), sq(14), SOLID),
  path(
    "heart",
    L("Kalp", "Heart", "Corazón"),
    "M50 92C20 70 3 52 3 32C3 16 15 5 29 5C39 5 46 11 50 18C54 11 61 5 71 5C85 5 97 16 97 32C97 52 80 70 50 92Z"
  ),
  poly("plus", L("Artı", "Plus", "Más"), [
    [0.35, 0],
    [0.65, 0],
    [0.65, 0.35],
    [1, 0.35],
    [1, 0.65],
    [0.65, 0.65],
    [0.65, 1],
    [0.35, 1],
    [0.35, 0.65],
    [0, 0.65],
    [0, 0.35],
    [0.35, 0.35],
  ]),
  poly(
    "bolt",
    L("Şimşek", "Lightning", "Rayo"),
    [
      [0.58, 0],
      [0.1, 0.58],
      [0.45, 0.58],
      [0.35, 1],
      [0.9, 0.4],
      [0.55, 0.4],
      [0.72, 0],
    ],
    box(10, 26),
    SOLID
  ),
  path(
    "drop",
    L("Damla", "Drop", "Gota"),
    "M50 4C50 4 88 48 88 66C88 86 71 97 50 97C29 97 12 86 12 66C12 48 50 4 50 4Z",
    box(12, 26)
  ),
  poly(
    "burst",
    L("Patlama", "Burst", "Explosión"),
    starPoints(12, 0, [1, 0.62, 0.9, 0.55, 1, 0.66, 0.86, 0.58]),
    sq(15),
    SOLID
  ),
  path(
    "wave",
    L("Dalga", "Wave", "Ola"),
    "M0 30C20 0 35 0 50 30C65 60 80 60 100 30V100H0Z",
    box(24, 22)
  ),
];

const SIMGELER = [
  icon("icon-book", L("Kitap", "Book", "Libro"), "book"),
  icon("icon-graduation", L("Mezuniyet", "Graduation", "Graduación"), "graduation"),
  icon("icon-school", L("Okul", "School", "Escuela"), "school"),
  icon("icon-teacher", L("Eğitmen", "Instructor", "Instructor"), "teacher"),
  icon("icon-student", L("Öğrenci", "Student", "Estudiante"), "student"),
  icon("icon-idea", L("Fikir", "Idea", "Idea"), "idea"),
  icon("icon-brain", L("Beyin", "Brain", "Cerebro"), "brain"),
  icon("icon-target", L("Hedef", "Target", "Objetivo"), "target"),
  icon("icon-correct", L("Doğru", "Correct", "Correcto"), "correct"),
  icon("icon-wrong", L("Yanlış", "Wrong", "Incorrecto"), "wrong"),
  icon("icon-warning", L("Uyarı", "Warning", "Advertencia"), "warning"),
  icon("icon-info", L("Bilgi", "Info", "Información"), "info"),
  icon("icon-question", L("Soru", "Question", "Pregunta"), "question"),
  icon("icon-hint", L("İpucu", "Hint", "Pista"), "hint"),
  icon("icon-play", L("Oynat", "Play", "Reproducir"), "play"),
  icon("icon-pause", L("Duraklat", "Pause", "Pausa"), "pause"),
  icon("icon-sound", L("Ses", "Sound", "Sonido"), "sound"),
  icon("icon-headphones", L("Kulaklık", "Headphones", "Auriculares"), "headphones"),
  icon("icon-video", L("Video", "Video", "Vídeo"), "video"),
  icon("icon-image", L("Görsel", "Image", "Imagen"), "image"),
  icon("icon-document", L("Belge", "Document", "Documento"), "document"),
  icon("icon-folder", L("Klasör", "Folder", "Carpeta"), "folder"),
  icon("icon-clipboard", L("Liste", "List", "Lista"), "clipboard"),
  icon("icon-checklist", L("Kontrol listesi", "Checklist", "Lista de control"), "checklist"),
  icon("icon-pen", L("Kalem", "Pen", "Bolígrafo"), "pen"),
  icon("icon-edit", L("Düzenle", "Edit", "Editar"), "edit"),
  icon("icon-calendar", L("Takvim", "Calendar", "Calendario"), "calendar"),
  icon("icon-clock", L("Saat", "Clock", "Reloj"), "clock"),
  icon("icon-hourglass", L("Kum saati", "Hourglass", "Reloj de arena"), "hourglass"),
  icon("icon-trophy", L("Kupa", "Trophy", "Trofeo"), "trophy"),
  icon("icon-medal", L("Madalya", "Medal", "Medalla"), "medal"),
  icon("icon-award", L("Ödül", "Award", "Premio"), "award"),
  icon("icon-flag", L("Bayrak", "Flag", "Bandera"), "flag"),
  icon("icon-rocket", L("Roket", "Rocket", "Cohete"), "rocket"),
  icon("icon-puzzle", L("Yapboz", "Puzzle", "Rompecabezas"), "puzzle"),
  icon("icon-gear", L("Ayarlar", "Settings", "Ajustes"), "gear"),
  icon("icon-search", L("Büyüteç", "Magnifier", "Lupa"), "search"),
  icon("icon-chart-line", L("Çizgi grafik", "Line chart", "Gráfico de líneas"), "chartLine"),
  icon("icon-chart-pie", L("Pasta grafik", "Pie chart", "Gráfico circular"), "chartPie"),
  icon("icon-team", L("Ekip", "Team", "Equipo"), "team"),
  icon("icon-person", L("Kişi", "Person", "Persona"), "person"),
  icon("icon-chat", L("Sohbet", "Chat", "Chat"), "chat"),
  icon("icon-mail", L("E-posta", "Email", "Correo"), "mail"),
  icon("icon-phone", L("Telefon", "Phone", "Teléfono"), "phone"),
  icon("icon-laptop", L("Dizüstü", "Laptop", "Portátil"), "laptop"),
  icon("icon-mobile", L("Mobil", "Mobile", "Móvil"), "mobile"),
  icon("icon-globe", L("Dünya", "Globe", "Mundo"), "globe"),
  icon("icon-lock", L("Kilit", "Lock", "Candado"), "lock"),
  icon("icon-key", L("Anahtar", "Key", "Llave"), "key"),
  icon("icon-shield", L("Güvenlik", "Security", "Seguridad"), "shield"),
  icon("icon-handshake", L("Anlaşma", "Handshake", "Acuerdo"), "handshake"),
  icon("icon-like", L("Beğeni", "Like", "Me gusta"), "like"),
];

const withCategory = (category, list) => list.map((s) => ({ ...s, category }));

export const SHAPES = [
  ...withCategory("temel", TEMEL),
  ...withCategory("kartlar", KARTLAR),
  ...withCategory("cizgiler", CIZGILER),
  ...withCategory("oklar", OKLAR),
  ...withCategory("cagri", CAGRI),
  ...withCategory("dekoratif", DEKORATIF),
  ...withCategory("simgeler", SIMGELER),
];

const BY_ID = SHAPES.reduce((acc, s) => {
  acc[s.id] = s;
  return acc;
}, {});

export const getShape = (id) => BY_ID[id];

export const shapesIn = (categoryKey) => SHAPES.filter((s) => s.category === categoryKey);

const langOf = (lang) => {
  const base = String(lang || "tr").split("-")[0];
  return ["tr", "en", "es"].includes(base) ? base : "tr";
};

export const shapeLabel = (shape, lang) =>
  shape ? shape.label[langOf(lang)] || shape.label.tr : "";

export const categoryLabel = (category, lang) =>
  category ? category.label[langOf(lang)] || category.label.tr : "";
